import { fetchWithAuth } from "@/hooks/useAuth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type FileError,
  type FileRejection,
  type FileWithPath,
  useDropzone,
} from "react-dropzone";

interface FileWithPreview extends File {
  preview?: string;
  errors: readonly FileError[];
}

type UseCloudflareUploadOptions = {
  path?: string; // kept for signature parity; unused here
  allowedMimeTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
};

export type UploadedResult = {
  fileName: string;
  imageId: string;
  publicUrl: string;
};

type UseCloudflareUploadReturn = {
  files: FileWithPreview[];
  setFiles: React.Dispatch<React.SetStateAction<FileWithPreview[]>>;
  successes: string[];
  isSuccess: boolean;
  loading: boolean;
  errors: { name: string; message: string }[];
  setErrors: React.Dispatch<
    React.SetStateAction<{ name: string; message: string }[]>
  >;
  onUpload: () => Promise<void>;
  maxFileSize: number;
  maxFiles: number;
  allowedMimeTypes: string[];
  // react-dropzone passthrough
  getRootProps: ReturnType<typeof useDropzone>["getRootProps"];
  getInputProps: ReturnType<typeof useDropzone>["getInputProps"];
  open: () => void;
  isFocused: boolean;
  isDragActive: boolean;
  isDragAccept: boolean;
  isDragReject: boolean;
  isFileDialogActive: boolean;
  acceptedFiles: readonly FileWithPath[];
  fileRejections: readonly FileRejection[];
  rootRef: React.RefObject<HTMLElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  // extra
  uploadedResults: UploadedResult[];
};

const CF_BASE = import.meta.env.VITE_CF_IMAGES_BASE_URL as string;

export function useCloudflareUpload(
  options: UseCloudflareUploadOptions
): UseCloudflareUploadReturn {
  const {
    allowedMimeTypes = [],
    maxFileSize = Number.POSITIVE_INFINITY,
    maxFiles = 1,
  } = options;

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ name: string; message: string }[]>([]);
  const [successes, setSuccesses] = useState<string[]>([]);
  const [uploadedResults, setUploadedResults] = useState<UploadedResult[]>([]);

  const isSuccess = useMemo(() => {
    if (errors.length === 0 && successes.length === 0) return false;
    if (errors.length === 0 && successes.length === files.length) return true;
    return false;
  }, [errors.length, successes.length, files.length]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const validFiles = acceptedFiles
        .filter((file) => !files.find((x) => x.name === file.name))
        .map((file) => {
          (file as FileWithPreview).preview = URL.createObjectURL(file);
          (file as FileWithPreview).errors = [];
          return file as FileWithPreview;
        });

      const invalidFiles = fileRejections.map(({ file, errors }) => {
        (file as FileWithPreview).preview = URL.createObjectURL(file);
        (file as FileWithPreview).errors = errors;
        return file as FileWithPreview;
      });

      setFiles((prev) => [...prev, ...validFiles, ...invalidFiles]);
    },
    [files]
  );

  const dropzone = useDropzone({
    onDrop,
    noClick: true,
    accept: allowedMimeTypes.reduce(
      (acc, type) => ({ ...acc, [type]: [] }),
      {}
    ),
    maxSize: maxFileSize,
    maxFiles,
    multiple: maxFiles !== 1,
  });

  const onUpload = useCallback(async () => {
    setLoading(true);

    const filesWithErrors = errors.map((x) => x.name);
    const filesToUpload =
      filesWithErrors.length > 0
        ? [
            ...files.filter((f) => filesWithErrors.includes(f.name)),
            ...files.filter((f) => !successes.includes(f.name)),
          ]
        : files;

    const newErrors: { name: string; message: string }[] = [];
    const newSuccesses: string[] = [];
    const newResults: UploadedResult[] = [];

    for (const file of filesToUpload) {
      try {
        // 1) Ask backend for a direct-upload URL
        const createRes = await fetchWithAuth(
          `/api/admin/uploads/images/direct-upload`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileName: file.name,
            }),
          }
        );
        if (!createRes.ok) {
          const err = await createRes.json().catch(() => ({}));
          throw new Error(err.message || "Eroare creare upload URL");
        }
        const { uploadURL } = await createRes.json();

        // 2) POST actual file to Cloudflare using the signed uploadURL
        const form = new FormData();
        // Ensure filename is included so the browser sets proper multipart boundaries and disposition
        form.append("file", file, file.name);
        const uploadRes = await fetch(uploadURL, {
          method: "POST",
          body: form,
        });
        // Try to parse CF response to extract image id / variants
        let uploadJson: any = null;
        try {
          uploadJson = await uploadRes.json();
        } catch (_) {
          uploadJson = null;
        }

        if (!uploadRes.ok) {
          const msg =
            uploadJson?.errors?.[0]?.message ||
            uploadJson?.message ||
            "Eroare upload CF";
          throw new Error(msg);
        }

        const imageId: string | undefined =
          uploadJson?.result?.id || uploadJson?.id || undefined;
        const variants: string[] | undefined =
          uploadJson?.result?.variants || uploadJson?.variants;

        let publicUrl: string | undefined = undefined;
        if (imageId && CF_BASE) {
          publicUrl = `${CF_BASE}/${imageId}/public`;
        } else if (variants && variants.length > 0) {
          publicUrl = variants[0];
        }

        if (!publicUrl || !imageId) {
          throw new Error("Upload reușit, dar nu am primit ID-ul imaginii");
        }

        newSuccesses.push(file.name);
        newResults.push({ fileName: file.name, imageId, publicUrl });
      } catch (e: any) {
        newErrors.push({ name: file.name, message: e?.message || "Eroare" });
      }
    }

    setErrors(newErrors);
    setSuccesses((prev) => Array.from(new Set([...prev, ...newSuccesses])));
    setUploadedResults((prev) => [...prev, ...newResults]);

    setLoading(false);
  }, [files, errors, successes]);

  useEffect(() => {
    if (files.length === 0) setErrors([]);
    if (files.length <= maxFiles) {
      let changed = false;
      const newFiles = files.map((file) => {
        if (file.errors.some((e) => e.code === "too-many-files")) {
          file.errors = file.errors.filter((e) => e.code !== "too-many-files");
          changed = true;
        }
        return file;
      });
      if (changed) setFiles(newFiles);
    }
  }, [files.length, maxFiles]);

  return {
    files,
    setFiles,
    successes,
    isSuccess,
    loading,
    errors,
    setErrors,
    onUpload,
    maxFileSize,
    maxFiles,
    allowedMimeTypes,
    getRootProps: dropzone.getRootProps,
    getInputProps: dropzone.getInputProps,
    isDragActive: dropzone.isDragActive,
    isDragReject: dropzone.isDragReject,
    // Expose additional react-dropzone fields to match existing Dropzone expectations
    open: dropzone.open,
    isFocused: dropzone.isFocused,
    isDragAccept: dropzone.isDragAccept,
    isFileDialogActive: dropzone.isFileDialogActive,
    acceptedFiles: dropzone.acceptedFiles,
    fileRejections: dropzone.fileRejections,
    rootRef: dropzone.rootRef,
    inputRef: dropzone.inputRef,
    uploadedResults,
  };
}
